import { parseSanitizedHtml } from "@/utils/sanitizeHtml";
import { useEffect, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { DateTime } from "luxon";
import { toast } from "react-toastify";
import { SlSizeFullscreen } from "react-icons/sl";
import { AiOutlineFullscreenExit } from "react-icons/ai";
import RightTimeApi from "@/API";
import Modal from "@modalSrc/Modal";
import ModalLanguageSelector from "@modalSrc/ModalLanguageSelector";
import ContentVersionHistoryButton from "@modalSrc/ContentVersionHistoryButton";
import ContentVersionHistorySelect from "@modalSrc/ContentVersionHistorySelect";
import {
    findContentVersionById,
    getCurrentContentVersionId,
    isViewingHistoricalContentVersion,
    mergeContentVersionNote,
} from "@/utils/contentVersionHistory";
import "@/style/sass/_contentVersionHistorySelect.scss";
import SectionRenderer, { canRenderSection } from "@/sections/SectionRenderer";
import type { SectionRealtimePayload, SectionCompletedEvent, SectionRendererHooks } from "@/sections/sectionRuntimeTypes";
import Helpers from "@/Helpers";
import TestOutputDataModal from "@/elements/modals/TestOutputDataModal";
import ConfirmModal from "@modals/ConfirmModal";
import {
    filterActiveLanguages,
    getLanguageDisplayName,
    getLanguageId,
    getTranslationLocale,
    resolveLanguageIdFromAbbrev,
    resolveLanguageMeta,
    toTranslationLanguageLike,
    type LanguageLike,
} from "@/utils/languageUtils";
import { translateFromEnglishAsync as requestTranslationFromEnglish } from "@/utils/translationService";
import { apiMessage } from "@/utils/apiMessage";
import { applyHtmlEditorFieldDrafts } from "@/utils/sectionEditorDrafts";
import { realtimeEventsAfterSectionComplete } from "@/utils/testSectionCompletionHelpers";

export default function SectionEditModal(props) {
    const CORE_HTML_FIELD_KEYS = ["instructions", "instructionsSecondary", "additionalInstructions", "reminder"];

    const {
        isOpen,
        onClose,
        languageId,
        languageAbbrev,
        sectionContentId,
        companyTestTypeId,
        sectionId,
        masterSectionContentId,
        onSectionContentForked,
        updateSpinner,
    } = props;

    const [testState, updateTestState] = useState(null);
    const [originalSection, setOriginalSection] = useState(null);
    const [textareaValue, setTextareaValue] = useState("");
    const [nameValue, setNameValue] = useState("");
    const [draftContentByLanguage, setDraftContentByLanguage] = useState({});
    const [draftElementsByLanguage, setDraftElementsByLanguage] = useState({});
    const [draftHtmlByLanguage, setDraftHtmlByLanguage] = useState({});
    const [draftHtmlFieldsByLanguage, setDraftHtmlFieldsByLanguage] = useState({});
    const [selectedHtmlField, setSelectedHtmlField] = useState("instructions");
    const [selectedHtmlText, setSelectedHtmlText] = useState("");
    const [languageDefinitions, setLanguageDefinitions] = useState([]);
    const textareaRef = useRef(null);
    const [previewSection, setPreviewSection] = useState(null);
    const [payloadPreview, setPayloadPreview] = useState(null);
    const [previewRuntimeResetKey, setPreviewRuntimeResetKey] = useState(0);
    const previewRealtimePayloadsRef = useRef([]);
    const previewOopsCountRef = useRef(0);
    const [showJsonEditor, setShowJsonEditor] = useState(false);
    const [leftPanelWidth, setLeftPanelWidth] = useState(25);
    const [isViewportExpanded, setIsViewportExpanded] = useState(false);
    const [isHtmlEditorOpen, setIsHtmlEditorOpen] = useState(false);
    const [htmlEditorField, setHtmlEditorField] = useState("");
    const [htmlEditorLanguageKey, setHtmlEditorLanguageKey] = useState("");
    const [htmlEditorDraftByLanguage, setHtmlEditorDraftByLanguage] = useState({});
    const [htmlEditorOriginalDraftByLanguage, setHtmlEditorOriginalDraftByLanguage] = useState({});
    const [htmlEditorLeftPanelWidth, setHtmlEditorLeftPanelWidth] = useState(50);
    const [isHtmlEditorTranslating, setIsHtmlEditorTranslating] = useState(false);
    const [showHtmlEditorCloseConfirm, setShowHtmlEditorCloseConfirm] = useState(false);
    const [showSectionCloseConfirm, setShowSectionCloseConfirm] = useState(false);
    const [originalPersistedSnapshot, setOriginalPersistedSnapshot] = useState(null);
    const [activeSectionContentId, setActiveSectionContentId] = useState(null);
    const [versionsByLanguageId, setVersionsByLanguageId] = useState({});
    const [selectedVersionIdByLanguageId, setSelectedVersionIdByLanguageId] = useState({});
    const [historyPreviewContentByLanguage, setHistoryPreviewContentByLanguage] = useState({});
    const draftHtmlByLanguageRef = useRef({});
    const draftElementsByLanguageRef = useRef({});
    const [previewSectionControlsByAbbrev, setPreviewSectionControlsByAbbrev] = useState<Record<string, unknown>>({});
    const splitContainerRef = useRef(null);
    const htmlEditorSplitContainerRef = useRef(null);
    const activeLanguageKeyRef = useRef("");

    useEffect(() => {
        activeLanguageKeyRef.current = testState?.lang || "";
    }, [testState?.lang]);

    useEffect(() => {
        if (!isOpen) {
            resetModalState();
            return;
        }

        if (!sectionContentId) {
            resetModalState();
            return;
        }

        setActiveSectionContentId(Number(sectionContentId) || null);
        resetModalState();

        loadSectionAsync(Number(sectionContentId));
        void loadSectionControlsAsync();
    }, [isOpen, sectionContentId, languageId, languageAbbrev]);

    async function loadSectionControlsAsync() {
        try {
            const resp = await RightTimeApi.getAsync("/SectionControls");
            if (resp?.ok && Array.isArray(resp.jsonData)) {
                setPreviewSectionControlsByAbbrev(
                    Helpers.buildSectionControlsByAbbrevFromRows(resp.jsonData),
                );
            } else {
                setPreviewSectionControlsByAbbrev({});
            }
        } catch {
            setPreviewSectionControlsByAbbrev({});
        }
    }

    useEffect(() => {
        if (!splitContainerRef.current) return;
        splitContainerRef.current.style.setProperty("--section-editor-left-width", `${leftPanelWidth}%`);
    }, [leftPanelWidth, showJsonEditor]);

    useEffect(() => {
        if (!htmlEditorSplitContainerRef.current) return;
        htmlEditorSplitContainerRef.current.style.setProperty("--content-edit-left-width", `${htmlEditorLeftPanelWidth}%`);
    }, [htmlEditorLeftPanelWidth, isHtmlEditorOpen]);

    function resetModalState() {
        updateTestState(null);
        setOriginalSection(null);
        setTextareaValue("");
        setNameValue("");
        setActiveSectionContentId(null);
        setDraftContentByLanguage({});
        setDraftElementsByLanguage({});
        setDraftHtmlByLanguage({});
        draftHtmlByLanguageRef.current = {};
        draftElementsByLanguageRef.current = {};
        setDraftHtmlFieldsByLanguage({});
        setSelectedHtmlField("instructions");
        setSelectedHtmlText("");
        setLanguageDefinitions([]);
        setPreviewSection(null);
        setPayloadPreview(null);
        previewRealtimePayloadsRef.current = [];
        previewOopsCountRef.current = 0;
        setShowJsonEditor(false);
        setIsViewportExpanded(false);
        setIsHtmlEditorOpen(false);
        setHtmlEditorField("");
        setHtmlEditorLanguageKey("");
        setHtmlEditorDraftByLanguage({});
        setHtmlEditorOriginalDraftByLanguage({});
        setHtmlEditorLeftPanelWidth(50);
        setIsHtmlEditorTranslating(false);
        setShowHtmlEditorCloseConfirm(false);
        setShowSectionCloseConfirm(false);
        setOriginalPersistedSnapshot(null);
        setVersionsByLanguageId({});
        setSelectedVersionIdByLanguageId({});
        setHistoryPreviewContentByLanguage({});
    }

    function applyHistoricalDisplayForLanguage(languageKey, versionContent) {
        const parsed = parseCanonicalContent(versionContent);
        const formattedElements = Helpers.formatJsonSmart(parsed.elements || {}, myMaxLineLength, 2);
        const nextVisibleFields = parsed.htmlFields?.length ? parsed.htmlFields : ["instructions"];
        const nextSelectedField = nextVisibleFields.includes(selectedHtmlField)
            ? selectedHtmlField
            : (nextVisibleFields[0] || "instructions");
        setTextareaValue(formattedElements);
        setSelectedHtmlField(nextSelectedField);
        setSelectedHtmlText(nextSelectedField ? (parsed.htmlValues?.[nextSelectedField] || "") : "");

        // Drive the live preview from the selected history version without mutating editable drafts.
        const canonical = buildCanonicalRawContent(formattedElements, parsed.htmlValues || {});
        const previewContent = canonical.ok
            ? canonical.formatted
            : (typeof versionContent === "string" ? versionContent : JSON.stringify(versionContent ?? {}));
        setHistoryPreviewContentByLanguage({ [languageKey]: previewContent });
        setPreviewRuntimeResetKey((prev) => prev + 1);
    }

    function closePayloadPreview() {
        setPayloadPreview(null);
        previewRealtimePayloadsRef.current = [];
        previewOopsCountRef.current = 0;
        // Ensure preview runtime state is clean before taking the section again.
        setPreviewRuntimeResetKey((prev) => prev + 1);
    }



    function getSupportedLanguageCatalog(section = null) {
        const sourceSection = section || originalSection || testState?.currentSection;
        const fromSection = filterActiveLanguages(getContentArray(sourceSection));
        if (fromSection.length > 0) return fromSection;
        return languageDefinitions.map((item) => ({
            languageId: item.languageId,
            abbrev: item.key,
            name: item.languageName,
        }));
    }

    function toAbbrev(item) {
        const meta = resolveLanguageMeta(item, getSupportedLanguageCatalog());
        return meta?.key || "";
    }

    function toLanguageName(item, key) {
        const name = String(item?.name || item?.languageName || "").trim();
        if (name) return name;

        const catalog = getSupportedLanguageCatalog();
        const match = catalog.find((lang) => String(lang?.abbrev || "").trim().toLowerCase() === String(key || "").trim().toLowerCase());
        return getLanguageDisplayName(match || { abbrev: key });
    }

    function resolveLanguageRecordForKey(key) {
        const normalizedKey = String(key || "").trim().toLowerCase();
        const catalog = getSupportedLanguageCatalog();
        const match = catalog.find((lang) => String(lang?.abbrev || "").trim().toLowerCase() === normalizedKey);
        if (match) {
            return {
                languageId: getLanguageId(match),
                abbrev: String(match?.abbrev || normalizedKey).trim().toLowerCase(),
                name: getLanguageDisplayName(match),
            };
        }

        return {
            languageId: resolveLanguageIdFromAbbrev(normalizedKey, catalog),
            abbrev: normalizedKey,
            name: getLanguageDisplayName({ abbrev: normalizedKey }),
        };
    }

    function getContentArray(section) {
        return Array.isArray(section?.languages) ? section.languages : [];
    }

    function getContentsArray(section) {
        return Array.isArray(section?.contents) ? section.contents : [];
    }

    function getSectionContentName(section) {
        return typeof section?.sectionContentName === "string" ? section.sectionContentName : "";
    }

    function normalizeLanguageCatalogItem(item: LanguageLike) {
        return {
            languageId: getLanguageId(item),
            abbrev: String(item?.abbrev || "").trim().toLowerCase(),
            name: String(item?.name || "").trim(),
            display: String(item?.display || item?.Display || "").trim(),
            ietf: String(item?.ietf || item?.Ietf || item?.IETF || "").trim(),
            active: item?.active ?? item?.Active ?? true,
            supported: item?.supported ?? item?.Supported ?? true,
        };
    }

    function getLanguageDefinitions(section, activeLanguageCatalog: LanguageLike[] = []) {
        const sectionCatalog = filterActiveLanguages(getContentArray(section));
        const catalog = Array.isArray(activeLanguageCatalog) && activeLanguageCatalog.length > 0
            ? filterActiveLanguages(activeLanguageCatalog)
            : sectionCatalog;

        return catalog
            .map((item) => {
                const meta = resolveLanguageMeta(item, catalog);
                const key = meta?.key || toAbbrev(item);
                if (!key) return null;
                return {
                    key,
                    languageId: meta?.languageId || getLanguageId(item),
                    languageName: getLanguageDisplayName(item) || toLanguageName(item, key),
                };
            })
            .filter(Boolean)
            .sort((left, right) => left.languageId - right.languageId);
    }

    function getContentItem(section, languageKey) {
        return getContentsArray(section).find((item) => toAbbrev(item) === languageKey) || null;
    }

    function orderHtmlFieldKeys(fields) {
        const unique = Array.from(new Set((Array.isArray(fields) ? fields : []).filter((field) => typeof field === "string" && field.trim())));
        return unique.sort((left, right) => {
            const leftCoreIndex = CORE_HTML_FIELD_KEYS.indexOf(left);
            const rightCoreIndex = CORE_HTML_FIELD_KEYS.indexOf(right);

            if (leftCoreIndex >= 0 && rightCoreIndex >= 0) return leftCoreIndex - rightCoreIndex;
            if (leftCoreIndex >= 0) return -1;
            if (rightCoreIndex >= 0) return 1;
            return left.localeCompare(right);
        });
    }

    function parseCanonicalContent(value) {
        const parsed = typeof value === "object" && value !== null
            ? value
            : Helpers.tryParseJson(value);

        if (!parsed || typeof parsed !== "object") {
            return {
                elements: {},
                htmlValues: {
                    instructions: typeof value === "string" ? value : "",
                },
                htmlFields: typeof value === "string" && value ? ["instructions"] : [],
                parsed: null,
            };
        }

        const elements = (parsed?.elements && typeof parsed.elements === "object" && !Array.isArray(parsed.elements))
            ? parsed.elements
            : {};
        const html = (parsed?.html && typeof parsed.html === "object" && !Array.isArray(parsed.html))
            ? parsed.html
            : {};

        const htmlFields = orderHtmlFieldKeys(Object.keys(html));
        const htmlValues = htmlFields.reduce((acc, field) => {
            acc[field] = Helpers.decodeBase64Text(html?.[field]);
            return acc;
        }, {});

        return { elements, htmlValues, htmlFields, parsed };
    }

    function encodeHtmlFields(htmlValues) {
        const encoded = {};
        Object.keys(htmlValues || {}).forEach((field) => {
            const value = typeof htmlValues?.[field] === "string" ? htmlValues[field] : "";
            encoded[field] = value.trim().length > 0 ? Helpers.encodeBase64Text(value) : null;
        });
        return encoded;
    }

    /**
     * The Elements JSON pane is already the contents of `elements`.
     * Users often paste a full-schema fragment like `{ "elements": { "buttons": ... } }`,
     * which would nest as `elements.elements` and break button overrides. Unwrap that.
     */
    function normalizeElementsEditorPayload(parsed) {
        if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
            return parsed ?? {};
        }

        const nested = parsed.elements;
        if (!nested || typeof nested !== "object" || Array.isArray(nested)) {
            return parsed;
        }

        const topKeys = Object.keys(parsed);
        const looksLikeFullSchema =
            Object.prototype.hasOwnProperty.call(parsed, "schemaVersion")
            || Object.prototype.hasOwnProperty.call(parsed, "html");
        const onlyElementsWrapper = topKeys.length === 1 && topKeys[0] === "elements";

        if (looksLikeFullSchema || onlyElementsWrapper) {
            return nested;
        }

        return parsed;
    }

    function buildCanonicalRawContent(elementsText, htmlValues) {
        const result = Helpers.parseJsonLenient(typeof elementsText === "string" ? elementsText : "{}");
        if (!result.ok) {
            return { ok: false, error: result.error || "Invalid elements JSON", parsed: null, minified: "", formatted: "" };
        }

        const canonical = {
            schemaVersion: 2,
            html: encodeHtmlFields(htmlValues),
            elements: normalizeElementsEditorPayload(result.parsed),
        };

        return {
            ok: true,
            error: null,
            parsed: canonical,
            minified: JSON.stringify(canonical),
            formatted: Helpers.formatJsonSmart(canonical, myMaxLineLength, 2),
        };
    }

    function syncSectionDraftRefs(nextHtmlByLanguage, nextElementsByLanguage = draftElementsByLanguageRef.current) {
        draftHtmlByLanguageRef.current = nextHtmlByLanguage;
        draftElementsByLanguageRef.current = nextElementsByLanguage;
    }

    function getHtmlValuesForLanguage(languageKey) {
        const source = draftHtmlByLanguageRef.current ?? draftHtmlByLanguage;
        return {
            ...(source?.[languageKey] || {}),
        };
    }

    function buildContentByLanguageMap(section) {
        const fullMap = {};
        const elementsMap = {};
        const htmlMap = {};
        const htmlFieldMap = {};
        getContentsArray(section).forEach((item) => {
            const key = toAbbrev(item);
            if (!key) return;
            const parsed = parseCanonicalContent(item?.content);
            fullMap[key] = getEditorContent(item?.content);
            elementsMap[key] = Helpers.formatJsonSmart(parsed.elements || {}, myMaxLineLength, 2);
            htmlMap[key] = parsed.htmlValues;
            htmlFieldMap[key] = parsed.htmlFields || [];
        });
        return { fullMap, elementsMap, htmlMap, htmlFieldMap };
    }

    function getVisibleHtmlFieldsForLanguage(languageKey) {
        const explicitFields = draftHtmlFieldsByLanguage?.[languageKey];
        if (Array.isArray(explicitFields)) {
            return explicitFields;
        }

        const values = draftHtmlByLanguage?.[languageKey] || {};
        return orderHtmlFieldKeys(Object.keys(values).filter((field) => {
            const value = values?.[field];
            return typeof value === "string" && value.trim().length > 0;
        }));
    }

    function getExistingHtmlFieldsAcrossLanguages() {
        const existing = new Set();
        const languageKeys = Array.from(new Set([
            ...Object.keys(draftHtmlFieldsByLanguage || {}),
            ...Object.keys(draftHtmlByLanguage || {}),
            ...languageDefinitions.map((item) => item.key),
        ]));

        languageKeys.forEach((langKey) => {
            getVisibleHtmlFieldsForLanguage(langKey).forEach((field) => existing.add(field));
        });

        return orderHtmlFieldKeys(Array.from(existing));
    }

    function getDerivedPagesFromRawContent(rawContent) {
        const parsed = typeof rawContent === "object" && rawContent !== null
            ? rawContent
            : Helpers.tryParseJson(rawContent);
        const items = Array.isArray(parsed?.elements?.items) ? parsed.elements.items : [];
        const maxPage = items.reduce((highest, element) => {
            const pageNumber = Number(element?.page || 0);
            return Number.isFinite(pageNumber) ? Math.max(highest, pageNumber) : highest;
        }, 0);
        return maxPage > 0 ? maxPage : 1;
    }

    function resolveSectionPages(section, contentByLanguageOverride = null) {
        const storedPages = Number(section?.pages || 0);
        const rawContentByLanguage = toContentByLanguage(section, contentByLanguageOverride);
        let derivedPages = 1;
        for (const rawContent of Object.values(rawContentByLanguage)) {
            derivedPages = Math.max(derivedPages, getDerivedPagesFromRawContent(typeof rawContent === "string" ? rawContent : "{}"));
        }

        return Math.max(storedPages > 0 ? storedPages : 1, derivedPages);
    }

    const myMaxLineLength = 200;

    function getEditorContent(value) {
        return Helpers.formatJsonForEditor(value, myMaxLineLength, 2);
    }

    function getAllDraftLanguageKeys() {
        const htmlDrafts = draftHtmlByLanguageRef.current ?? draftHtmlByLanguage;
        const elementsDrafts = draftElementsByLanguageRef.current ?? draftElementsByLanguage;
        return Array.from(new Set([
            ...Object.keys(elementsDrafts || {}),
            ...Object.keys(htmlDrafts || {}),
            ...languageDefinitions.map((item) => item.key),
        ])).filter(Boolean);
    }

    function buildPersistedSnapshot(sectionContentName, elementsMap, htmlMap, languageKeys) {
        const minifiedByLanguage = {};
        for (const key of languageKeys) {
            const canonical = buildCanonicalRawContent(elementsMap?.[key] ?? "{}", htmlMap?.[key] ?? {});
            minifiedByLanguage[key] = canonical.ok
                ? canonical.minified
                : `__invalid__:${elementsMap?.[key] ?? ""}`;
        }

        return {
            sectionContentName: typeof sectionContentName === "string" ? sectionContentName : "",
            minifiedByLanguage,
        };
    }

    function handleCloseModal() {
        resetModalState();
        if (typeof onClose === "function") onClose();
    }

    function handleCloseModalAttempt() {
        if (isHtmlEditorOpen) {
            handleHtmlEditorCloseAttempt();
            return;
        }

        if (hasChanges) {
            setShowSectionCloseConfirm(true);
            return;
        }

        handleCloseModal();
    }

    function handleSectionCloseDiscard() {
        setShowSectionCloseConfirm(false);
        handleCloseModal();
    }

    async function handleSectionCloseSave() {
        setShowSectionCloseConfirm(false);
        const saved = await onSaveSectionData();
        if (saved) {
            handleCloseModal();
        }
    }

    function enterViewportExpandedMode() {
        setIsViewportExpanded(true);
    }

    function exitViewportExpandedMode() {
        setIsViewportExpanded(false);
    }

    function onSplitterMouseDown(e) {
        e.preventDefault();

        const onMouseMove = (moveEvent) => {
            const container = splitContainerRef.current;
            if (!container) return;

            const rect = container.getBoundingClientRect();
            const offsetX = moveEvent.clientX - rect.left;
            const percentage = (offsetX / rect.width) * 100;
            const clamped = Math.min(75, Math.max(25, percentage));
            setLeftPanelWidth(clamped);
        };

        const onMouseUp = () => {
            window.removeEventListener("mousemove", onMouseMove);
            window.removeEventListener("mouseup", onMouseUp);
            document.body.style.cursor = "";
            document.body.style.userSelect = "";
        };

        document.body.style.cursor = "col-resize";
        document.body.style.userSelect = "none";
        window.addEventListener("mousemove", onMouseMove);
        window.addEventListener("mouseup", onMouseUp);
    }

    function buildCustomCopyName(baseName) {
        const trimmedBaseName = typeof baseName === "string" ? baseName.trim() : "";
        const base = trimmedBaseName || "Section Content";
        return `${base} (Custom)`;
    }

    async function copySectionContentForCompanyTestTypeAsync(sourceSectionContentId, resolvedSectionId, requestedName) {
        const cttId = Number(companyTestTypeId || 0);
        if (!cttId || !sourceSectionContentId || !resolvedSectionId) {
            return null;
        }

        const copyResp = await RightTimeApi.postAsync(
            `/SectionContent/${sourceSectionContentId}/copyForCompanyTestType/${cttId}`,
            {
                sectionId: resolvedSectionId,
                name: requestedName,
            }
        );

        if (!copyResp?.ok) {
            const message = apiMessage(copyResp, "Unable to create a custom section content copy.");
            throw new Error(message);
        }

        const newSectionContentId = Number(copyResp?.jsonData?.sectionContentId || copyResp?.jsonData?.id || 0);
        if (!newSectionContentId) {
            throw new Error("Section content copy did not return a valid id.");
        }

        return {
            sectionContentId: newSectionContentId,
            sectionContentName: copyResp?.jsonData?.sectionContentName || requestedName,
            sectionId: Number(copyResp?.jsonData?.sectionId || resolvedSectionId),
        };
    }

    async function loadSectionAsync(sectionContentIdOverride = null) {
        const targetSectionContentId = Number(sectionContentIdOverride || activeSectionContentId || sectionContentId || 0);
        if (!isOpen || !targetSectionContentId) return;

        if (typeof updateSpinner === "function") updateSpinner(true);
        try {
            const resp = await RightTimeApi.getAsync(`/SectionContent/${targetSectionContentId}`);

            if (!resp?.ok || !resp?.jsonData) {
                toast.error("Failed to load section content.");
                return;
            }

            const data = resp.jsonData;

            let activeLanguageCatalog: LanguageLike[] = [];
            try {
                const langResp = await RightTimeApi.getAsync("/Languages?activeOnly=true");
                if (langResp?.ok && Array.isArray(langResp.jsonData)) {
                    activeLanguageCatalog = langResp.jsonData;
                }
            } catch {
                activeLanguageCatalog = [];
            }

            const section = {
                id: Number(data?.sectionId || data?.id || 0),
                sectionId: Number(data?.sectionId || 0),
                name: typeof data?.sectionName === "string" ? data.sectionName : "",
                sectionName: typeof data?.sectionName === "string" ? data.sectionName : "",
                order: Number(data?.order || 0),
                pages: Number(data?.pages) > 0 ? Number(data.pages) : 1,
                sectionContentId: Number(data?.sectionContentId || 0),
                sectionContentName: typeof data?.sectionContentName === "string" ? data.sectionContentName : "",
                usageCount: Number(data?.usageCount || 0),
                formId: Number(data?.formId || 0),
                languages: Array.isArray(activeLanguageCatalog) && activeLanguageCatalog.length > 0
                    ? activeLanguageCatalog.map((item) => normalizeLanguageCatalogItem(item))
                    : Array.isArray(data?.languages)
                        ? data.languages.map((item) => normalizeLanguageCatalogItem(item))
                        : [],
                contents: Array.isArray(data?.contents)
                    ? data.contents.map((item) => ({
                        languageId: Number(item?.languageId || item?.id || 0),
                        abbrev: typeof item?.abbrev === "string" ? item.abbrev : "",
                        name: typeof item?.name === "string" ? item.name : "",
                        content: item?.content ?? null,
                        versions: Array.isArray(item?.versions) ? item.versions : [],
                    }))
                    : [],
            };

            const nextVersionsByLanguageId = {};
            const nextSelectedVersionIds = {};
            (section.contents || []).forEach((item) => {
                if (!item?.languageId) return;
                nextVersionsByLanguageId[item.languageId] = item.versions || [];
                nextSelectedVersionIds[item.languageId] = getCurrentContentVersionId(item.versions || []);
            });
            setVersionsByLanguageId(nextVersionsByLanguageId);
            setSelectedVersionIdByLanguageId(nextSelectedVersionIds);

            if (!section.sectionId || !section.sectionContentId) {
                toast.error("Selected section content was not found.");
                return;
            }

            const currentSectionObj = {
                ...section,
                id: Number(section.id || section.sectionId || 0),
                sectionId: Number(section.sectionId || section.id || 0),
                name: section.name || section.sectionName || "",
                sectionName: section.sectionName || section.name || "",
                formId: Number(section.formId || 0),
                pages: resolveSectionPages(section),
                order: Number(section.order || 0),
            };

            const availableLanguageDefs = getLanguageDefinitions(currentSectionObj, activeLanguageCatalog);
            const requestedLanguageKey = typeof languageAbbrev === "string"
                ? languageAbbrev.trim().toLowerCase()
                : "";
            const requestedLanguageId = Number(languageId);
            const initialLanguageId = Number.isFinite(requestedLanguageId) && requestedLanguageId > 0
                ? requestedLanguageId
                : 1;

            const preferredLanguage = availableLanguageDefs.find((item) => item.key === requestedLanguageKey)
                || availableLanguageDefs.find((item) => item.key === "eng")
                || availableLanguageDefs.find((item) => item.languageId === initialLanguageId)
                || availableLanguageDefs[0]
                || { key: "eng", languageId: 1, languageName: "English" };

            const initialContentByLanguage = buildContentByLanguageMap(currentSectionObj);
            const formattedPreferredElements = initialContentByLanguage.elementsMap[preferredLanguage.key] || "{}";
            const preferredHtmlValues = initialContentByLanguage.htmlMap[preferredLanguage.key] || {
                instructions: "",
            };
            const preferredHtmlFields = initialContentByLanguage.htmlFieldMap[preferredLanguage.key] || [];
            const initialSelectedField = preferredHtmlFields.includes(selectedHtmlField)
                ? selectedHtmlField
                : (preferredHtmlFields[0] || "");

            setTextareaValue(formattedPreferredElements);
            setSelectedHtmlField(initialSelectedField);
            setSelectedHtmlText(initialSelectedField ? (preferredHtmlValues[initialSelectedField] || "") : "");
            setNameValue(getSectionContentName(section));
            setDraftContentByLanguage(initialContentByLanguage.fullMap);
            setDraftElementsByLanguage(initialContentByLanguage.elementsMap);
            setDraftHtmlByLanguage(initialContentByLanguage.htmlMap);
            syncSectionDraftRefs(initialContentByLanguage.htmlMap, initialContentByLanguage.elementsMap);
            setDraftHtmlFieldsByLanguage(initialContentByLanguage.htmlFieldMap);
            setLanguageDefinitions(availableLanguageDefs);
            setOriginalSection(Helpers.deepClone(section));
            const loadedLanguageKeys = Array.from(new Set([
                ...Object.keys(initialContentByLanguage.elementsMap || {}),
                ...Object.keys(initialContentByLanguage.htmlMap || {}),
                ...availableLanguageDefs.map((item) => item.key),
            ])).filter(Boolean);
            setOriginalPersistedSnapshot(buildPersistedSnapshot(
                getSectionContentName(section),
                initialContentByLanguage.elementsMap,
                initialContentByLanguage.htmlMap,
                loadedLanguageKeys,
            ));
            updateTestState({
                currentSection: currentSectionObj,
                lang: preferredLanguage.key,
                languageId: preferredLanguage.languageId,
            });
        } catch (error) {
            console.log("Error loading section:", error);
            toast.error("Error loading section: " + (error?.message || "Unknown error"));
        } finally {
            if (typeof updateSpinner === "function") updateSpinner(false);
        }
    }

    function updateActiveLanguageContent(value, languageKeyOverride = null) {
        const activeKey = languageKeyOverride || testState?.lang || languageDefinitions?.[0]?.key || "eng";
        setTextareaValue(value);

        const nextElementsByLanguage = {
            ...(draftElementsByLanguageRef.current ?? draftElementsByLanguage),
            [activeKey]: value,
        };
        draftElementsByLanguageRef.current = nextElementsByLanguage;
        setDraftElementsByLanguage(nextElementsByLanguage);

        const htmlValues = getHtmlValuesForLanguage(activeKey);
        const canonical = buildCanonicalRawContent(value, htmlValues);
        if (!canonical.ok) return;

        setDraftContentByLanguage((prev) => ({
            ...prev,
            [activeKey]: canonical.formatted,
        }));
    }

    function getElementsTextForLanguage(languageKey) {
        const source = draftElementsByLanguageRef.current ?? draftElementsByLanguage;
        if (source?.[languageKey]) {
            return source[languageKey];
        }

        return Helpers.formatJsonSmart(parseCanonicalContent(getContentItem(testState?.currentSection, languageKey)?.content).elements || {}, myMaxLineLength, 2);
    }

    function getHtmlValuesSnapshotForLanguage(languageKey) {
        const draft = draftHtmlByLanguage?.[languageKey];
        if (draft) return { ...draft };
        return { ...(parseCanonicalContent(getContentItem(testState?.currentSection, languageKey)?.content).htmlValues || {}) };
    }

    function isElementsJsonEmpty(text) {
        const trimmed = String(text ?? "").trim();
        if (!trimmed) return true;
        const result = Helpers.parseJsonLenient(trimmed);
        // Invalid JSON is not "empty" — never overwrite something the user typed.
        if (!result.ok) return false;
        const parsed = result.parsed;
        if (parsed == null) return true;
        if (typeof parsed !== "object" || Array.isArray(parsed)) return false;
        return Object.keys(parsed).length === 0;
    }

    function hasAnyHtmlText(htmlValues) {
        return Object.values(htmlValues || {}).some((value) => typeof value === "string" && value.trim().length > 0);
    }

    function isLanguageContentEmpty(languageKey) {
        if (!isElementsJsonEmpty(getElementsTextForLanguage(languageKey))) return false;
        return !hasAnyHtmlText(getHtmlValuesSnapshotForLanguage(languageKey));
    }

    function applyLanguageContentDrafts(languageKey, elementsText, htmlValues, htmlFields) {
        const nextElementsByLanguage = {
            ...(draftElementsByLanguageRef.current ?? draftElementsByLanguage),
            [languageKey]: elementsText,
        };
        const nextHtmlByLanguage = {
            ...(draftHtmlByLanguageRef.current ?? draftHtmlByLanguage),
            [languageKey]: { ...htmlValues },
        };
        syncSectionDraftRefs(nextHtmlByLanguage, nextElementsByLanguage);
        setDraftElementsByLanguage(nextElementsByLanguage);
        setDraftHtmlByLanguage(nextHtmlByLanguage);
        setDraftHtmlFieldsByLanguage((prev) => ({ ...prev, [languageKey]: [...htmlFields] }));
        const canonical = buildCanonicalRawContent(elementsText, htmlValues);
        if (canonical.ok) {
            setDraftContentByLanguage((prev) => ({ ...prev, [languageKey]: canonical.formatted }));
        }
    }

    async function translateCopiedEnglishContentAsync(languageKey, englishElementsText, englishHtmlValues, selectedField) {
        const englishLanguageKey = resolveEnglishLanguageKey(languageDefinitions);
        const englishLanguage = languageDefinitions.find((item) => item.key === englishLanguageKey);
        const targetLanguage = languageDefinitions.find((item) => item.key === languageKey);

        const canonical = buildCanonicalRawContent(englishElementsText, englishHtmlValues);
        if (!canonical.ok) return;

        try {
            if (typeof updateSpinner === "function") updateSpinner(true);
            const resp = await RightTimeApi.postAsync("/Translate/SectionContent", {
                contentJson: canonical.minified,
                sourceLocale: getTranslationLocale(toTranslationLanguageLike(englishLanguage, englishLanguageKey), "en"),
                targetLocale: getTranslationLocale(toTranslationLanguageLike(targetLanguage, languageKey), "en"),
            });
            if (!resp?.ok) {
                throw new Error(apiMessage(resp, "Translation request failed"));
            }

            const translatedJson = String(resp?.jsonData?.translatedJson ?? resp?.jsonData?.TranslatedJson ?? "").trim();
            if (!translatedJson) throw new Error("Translation response was empty");

            const parsed = parseCanonicalContent(translatedJson);
            const translatedElementsText = Helpers.formatJsonSmart(parsed.elements || {}, myMaxLineLength, 2);
            const translatedHtmlValues = parsed.htmlValues || {};
            const translatedHtmlFields = parsed.htmlFields || [];
            applyLanguageContentDrafts(languageKey, translatedElementsText, translatedHtmlValues, translatedHtmlFields);

            // The spinner blocks language switches while translating, so the copied
            // language is still the active one; refresh the visible editor content.
            if (activeLanguageKeyRef.current === languageKey) {
                setTextareaValue(translatedElementsText);
                const nextField = translatedHtmlFields.includes(selectedField) ? selectedField : (translatedHtmlFields[0] || "");
                setSelectedHtmlField(nextField);
                setSelectedHtmlText(nextField ? (translatedHtmlValues[nextField] || "") : "");
            }
            toast.success("Copied the English content and translated it. Review, then Save to persist.");
        } catch (error) {
            console.error("Error translating copied section content:", error);
            toast.warn("Copied the English content, but automatic translation failed. The content was left in English.");
        } finally {
            if (typeof updateSpinner === "function") updateSpinner(false);
        }
    }

    function setLanguageByKey(newLang, newLangId = null) {
        const fallbackLangId = Number(languageDefinitions.find((item) => item.key === newLang)?.languageId || 0);
        const resolvedLangId = Number(newLangId ?? fallbackLangId);
        const englishLanguageKey = resolveEnglishLanguageKey(languageDefinitions);

        let nextElementsText = getElementsTextForLanguage(newLang);
        let nextHtmlValues = getHtmlValuesForLanguage(newLang);
        let nextVisibleFields = getVisibleHtmlFieldsForLanguage(newLang);
        let copiedEnglishDefault = false;

        if (newLang !== englishLanguageKey && isLanguageContentEmpty(newLang)) {
            const englishElementsText = getElementsTextForLanguage(englishLanguageKey);
            const englishHtmlValues = getHtmlValuesSnapshotForLanguage(englishLanguageKey);
            const englishHtmlFields = getVisibleHtmlFieldsForLanguage(englishLanguageKey);
            const englishHasContent = !isElementsJsonEmpty(englishElementsText) || hasAnyHtmlText(englishHtmlValues);

            if (englishHasContent) {
                copiedEnglishDefault = true;
                nextElementsText = englishElementsText;
                nextHtmlValues = englishHtmlValues;
                nextVisibleFields = englishHtmlFields;
                applyLanguageContentDrafts(newLang, englishElementsText, englishHtmlValues, englishHtmlFields);
            }
        }

        setTextareaValue(nextElementsText);

        const nextSelectedField = nextVisibleFields.includes(selectedHtmlField)
            ? selectedHtmlField
            : (nextVisibleFields[0] || "");
        setSelectedHtmlField(nextSelectedField);
        setSelectedHtmlText(nextSelectedField ? (nextHtmlValues[nextSelectedField] || "") : "");

        activeLanguageKeyRef.current = newLang;
        setHistoryPreviewContentByLanguage({});
        updateTestState((prev) => {
            if (!prev) return prev;
            return {
                ...prev,
                lang: newLang,
                languageId: Number.isNaN(resolvedLangId) ? (fallbackLangId || prev.languageId) : resolvedLangId,
            };
        });
        setSelectedVersionIdByLanguageId((prev) => ({
            ...prev,
            [Number.isNaN(resolvedLangId) ? fallbackLangId : resolvedLangId]:
                getCurrentContentVersionId(versionsByLanguageId[Number.isNaN(resolvedLangId) ? fallbackLangId : resolvedLangId] || []),
        }));

        if (copiedEnglishDefault) {
            void translateCopiedEnglishContentAsync(newLang, nextElementsText, nextHtmlValues, nextSelectedField);
        }
    }

    function onSectionEditorLanguageChange(languageKey: string) {
        if (!languageKey) return;
        const language = languageDefinitions.find((item) => item.key === languageKey);
        setLanguageByKey(languageKey, language?.languageId);
    }

    function onSectionHistoryVersionChange(versionId) {
        const languageId = Number(testState?.languageId || 0);
        if (!languageId) return;
        setSelectedVersionIdByLanguageId((prev) => ({
            ...prev,
            [languageId]: versionId,
        }));
        const versions = versionsByLanguageId[languageId] || [];
        const currentVersionId = getCurrentContentVersionId(versions);
        if (versionId == null || versionId === currentVersionId) {
            setHistoryPreviewContentByLanguage({});
            if (testState?.lang) {
                setLanguageByKey(testState.lang, languageId);
            }
            setPreviewRuntimeResetKey((prev) => prev + 1);
            return;
        }
        const version = findContentVersionById(versions, versionId);
        if (version?.content && testState?.lang) {
            applyHistoricalDisplayForLanguage(testState.lang, version.content);
        }
    }

    function onNameChange(e) {
        const newName = e.currentTarget.value;
        setNameValue(newName);
    }

    function onReformatJson() {
        const rawText = textareaValue || "";
        const result = Helpers.parseJsonLenient(rawText);
        if (!result.ok) {
            toast.error(`Invalid elements JSON format. ${result.error || "Please check your syntax."}`);
            return;
        }

        const normalized = normalizeElementsEditorPayload(result.parsed);
        const reformatted = Helpers.formatJsonSmart(normalized ?? {}, myMaxLineLength, 2);
        updateActiveLanguageContent(reformatted);
        toast.success("Elements JSON reformatted and common syntax issues fixed.");
    }

    function onHtmlFieldChange(e) {
        const field = e.currentTarget.dataset.field;
        if (!field) return;
        const activeKey = testState?.lang || languageDefinitions?.[0]?.key || "eng";
        const nextText = e.currentTarget.value;

        setSelectedHtmlText(nextText);
        setDraftHtmlByLanguage((prev) => {
            const currentValues = {
                ...(prev?.[activeKey] || {}),
            };
            const next = {
                ...prev,
                [activeKey]: {
                    ...currentValues,
                    [field]: nextText,
                },
            };

            setDraftHtmlFieldsByLanguage((existingFields) => {
                const currentFieldList = Array.isArray(existingFields?.[activeKey])
                    ? existingFields[activeKey]
                    : getVisibleHtmlFieldsForLanguage(activeKey);

                const shouldIncludeField = nextText.trim().length > 0;
                const updatedFieldSet = new Set(currentFieldList);
                if (shouldIncludeField) updatedFieldSet.add(field);
                else updatedFieldSet.delete(field);
                const updatedFieldList = orderHtmlFieldKeys(Array.from(updatedFieldSet));

                if (!updatedFieldSet.has(selectedHtmlField)) {
                    const fallbackField = updatedFieldList[0] || "";
                    setSelectedHtmlField(fallbackField);
                    setSelectedHtmlText(fallbackField ? (next?.[activeKey]?.[fallbackField] || "") : "");
                }

                return {
                    ...existingFields,
                    [activeKey]: updatedFieldList,
                };
            });

            const canonical = buildCanonicalRawContent(
                draftElementsByLanguageRef.current?.[activeKey]
                    ?? draftElementsByLanguage?.[activeKey]
                    ?? textareaValue,
                next[activeKey],
            );
            if (canonical.ok) {
                setDraftContentByLanguage((existing) => ({
                    ...existing,
                    [activeKey]: canonical.formatted,
                }));
            }

            draftHtmlByLanguageRef.current = next;
            return next;
        });
    }

    function onSelectHtmlField(e) {
        const field = e.currentTarget.dataset.field;
        if (!field) return;
        const activeKey = testState?.lang || languageDefinitions?.[0]?.key || "eng";
        setSelectedHtmlField(field);
        setSelectedHtmlText(getHtmlValuesForLanguage(activeKey)?.[field] || "");
    }

    function onHtmlTableEditClick(e) {
        const field = e.currentTarget.dataset.field;
        if (!field) return;

        const activeKey = testState?.lang || languageDefinitions?.[0]?.key || "eng";
        const initialDraft = {};
        languageDefinitions.forEach((language) => {
            initialDraft[language.key] = getHtmlValuesForLanguage(language.key)?.[field] || "";
        });

        setHtmlEditorField(field);
        setHtmlEditorLanguageKey(activeKey);
        setHtmlEditorDraftByLanguage(initialDraft);
        setHtmlEditorOriginalDraftByLanguage({ ...initialDraft });
        setHtmlEditorLeftPanelWidth(50);
        setShowHtmlEditorCloseConfirm(false);
        setIsHtmlEditorOpen(true);
    }

    function onHtmlEditorLanguageChange(languageKey: string) {
        if (!languageKey) return;
        setHtmlEditorLanguageKey(languageKey);
    }

    function onHtmlEditorChange(e) {
        const key = htmlEditorLanguageKey;
        if (!key) return;
        const nextText = e.currentTarget.value;
        setHtmlEditorDraftByLanguage((prev) => ({
            ...prev,
            [key]: nextText,
        }));
    }

    function onHtmlEditorSplitterMouseDown(e) {
        e.preventDefault();

        const onMouseMove = (moveEvent) => {
            const container = htmlEditorSplitContainerRef.current;
            if (!container) return;

            const rect = container.getBoundingClientRect();
            const offsetX = moveEvent.clientX - rect.left;
            const percentage = (offsetX / rect.width) * 100;
            const clamped = Math.min(75, Math.max(25, percentage));
            setHtmlEditorLeftPanelWidth(clamped);
        };

        const onMouseUp = () => {
            window.removeEventListener("mousemove", onMouseMove);
            window.removeEventListener("mouseup", onMouseUp);
            document.body.style.cursor = "";
            document.body.style.userSelect = "";
        };

        document.body.style.cursor = "col-resize";
        document.body.style.userSelect = "none";
        window.addEventListener("mousemove", onMouseMove);
        window.addEventListener("mouseup", onMouseUp);
    }

    function resolveEnglishLanguageKey(languageDefs) {
        if (!Array.isArray(languageDefs) || languageDefs.length === 0) return "eng";

        const fromKey = languageDefs.find((item) => String(item?.key || "").toLowerCase() === "eng");
        if (fromKey?.key) return fromKey.key;

        const fromId = languageDefs.find((item) => Number(item?.languageId) === 1);
        if (fromId?.key) return fromId.key;

        const fromName = languageDefs.find((item) => /english/i.test(String(item?.languageName || "")));
        if (fromName?.key) return fromName.key;

        return "eng";
    }

    async function onHtmlEditorTranslateAsync() {
        const englishLanguageKey = resolveEnglishLanguageKey(languageDefinitions);
        if (!htmlEditorLanguageKey || htmlEditorLanguageKey === englishLanguageKey) return;

        const englishContent = String(htmlEditorDraftByLanguage?.[englishLanguageKey] || "").trim();
        if (!englishContent) {
            toast.error("English content is required before translating.");
            return;
        }

        try {
            setIsHtmlEditorTranslating(true);
            const englishLanguage = languageDefinitions.find((item) => item.key === englishLanguageKey);
            const targetLanguage = languageDefinitions.find((item) => item.key === htmlEditorLanguageKey);
            const translatedText = await requestTranslationFromEnglish(
                englishContent,
                toTranslationLanguageLike(targetLanguage, htmlEditorLanguageKey),
                toTranslationLanguageLike(englishLanguage, englishLanguageKey),
            );

            setHtmlEditorDraftByLanguage((prev) => ({
                ...prev,
                [htmlEditorLanguageKey]: translatedText,
            }));
            toast.success("Content translated.");
        } catch (error) {
            console.error("Error translating section HTML:", error);
            toast.error("Error translating content.");
        } finally {
            setIsHtmlEditorTranslating(false);
        }
    }

    function areHtmlEditorDraftsEqual(left, right) {
        const keys = new Set([
            ...Object.keys(left || {}),
            ...Object.keys(right || {}),
        ]);
        for (const key of keys) {
            if ((left?.[key] ?? "") !== (right?.[key] ?? "")) return false;
        }
        return true;
    }

    function hasHtmlEditorUnsavedChanges() {
        return !areHtmlEditorDraftsEqual(htmlEditorOriginalDraftByLanguage, htmlEditorDraftByLanguage);
    }

    function closeHtmlEditorWithoutConfirm() {
        setIsHtmlEditorOpen(false);
        setHtmlEditorField("");
        setHtmlEditorLanguageKey("");
        setHtmlEditorDraftByLanguage({});
        setHtmlEditorOriginalDraftByLanguage({});
        setIsHtmlEditorTranslating(false);
        setShowHtmlEditorCloseConfirm(false);
    }

    function handleHtmlEditorCloseAttempt() {
        if (hasHtmlEditorUnsavedChanges()) {
            setShowHtmlEditorCloseConfirm(true);
            return;
        }
        closeHtmlEditorWithoutConfirm();
    }

    function onCloseHtmlEditor() {
        closeHtmlEditorWithoutConfirm();
    }

    function onSaveHtmlEditor() {
        const field = htmlEditorField;
        if (!field) {
            onCloseHtmlEditor();
            return;
        }

        const languageKeys = languageDefinitions.map((language) => language.key).filter(Boolean);
        const appliedDrafts = applyHtmlEditorFieldDrafts({
            field,
            htmlEditorDraftByLanguage,
            draftHtmlByLanguage: draftHtmlByLanguageRef.current ?? draftHtmlByLanguage,
            draftHtmlFieldsByLanguage,
            draftElementsByLanguage: draftElementsByLanguageRef.current ?? draftElementsByLanguage,
            draftContentByLanguage,
            languageKeys,
            getElementsTextForLanguage,
            getVisibleHtmlFieldsForLanguage,
            buildCanonicalRawContent,
            orderHtmlFieldKeys,
        });

        syncSectionDraftRefs(
            appliedDrafts.draftHtmlByLanguage,
            appliedDrafts.draftElementsByLanguage,
        );

        flushSync(() => {
            setDraftHtmlByLanguage(appliedDrafts.draftHtmlByLanguage);
            setDraftHtmlFieldsByLanguage(appliedDrafts.draftHtmlFieldsByLanguage);
            setDraftContentByLanguage(appliedDrafts.draftContentByLanguage);
            updateTestState((prev) => {
                if (!prev?.currentSection) return prev;
                return {
                    ...prev,
                    currentSection: applyContentByLanguageToSection(
                        prev.currentSection,
                        appliedDrafts.draftContentByLanguage,
                    ),
                };
            });
        });

        setPreviewRuntimeResetKey((prev) => prev + 1);

        const activeKey = testState?.lang || languageDefinitions?.[0]?.key || "eng";
        if (activeKey && field === selectedHtmlField) {
            setSelectedHtmlText(appliedDrafts.draftHtmlByLanguage?.[activeKey]?.[field] || "");
        }

        setHtmlEditorOriginalDraftByLanguage({ ...htmlEditorDraftByLanguage });
        closeHtmlEditorWithoutConfirm();
        toast.info("HTML changes applied. Click Save on the Section Content Editor to persist to the server.");
    }

    function buildComparableSection(section, contentNameOverride = undefined, contentByLanguageOverride = null) {
        if (!section) return null;

        const contents = getContentsArray(section).map((item) => {
            const key = toAbbrev(item);
            if (!key) return item;
            if (!contentByLanguageOverride || !Object.prototype.hasOwnProperty.call(contentByLanguageOverride, key)) {
                return item;
            }

            return {
                ...item,
                content: contentByLanguageOverride[key],
            };
        });

        if (contentByLanguageOverride) {
            Object.keys(contentByLanguageOverride).forEach((key) => {
                if (contents.some((item) => toAbbrev(item) === key)) return;
                const languageRecord = resolveLanguageRecordForKey(key);
                contents.push({
                    languageId: languageRecord.languageId,
                    abbrev: languageRecord.abbrev,
                    name: languageRecord.name,
                    content: contentByLanguageOverride[key],
                });
            });
        }

        return {
            ...section,
            contents,
            sectionContentName: contentNameOverride === undefined
                ? getSectionContentName(section)
                : contentNameOverride,
        };
    }

    const currentPersistedSnapshot = buildPersistedSnapshot(
        nameValue,
        draftElementsByLanguageRef.current ?? draftElementsByLanguage,
        draftHtmlByLanguageRef.current ?? draftHtmlByLanguage,
        getAllDraftLanguageKeys(),
    );
    const hasChanges = originalPersistedSnapshot !== null
        && JSON.stringify(currentPersistedSnapshot) !== JSON.stringify(originalPersistedSnapshot);

    function toContentByLanguage(section, contentByLanguageOverride = null) {
        const map = {};
        getContentsArray(section).forEach((item) => {
            const key = toAbbrev(item);
            if (!key) return;
            map[key] = typeof item?.content === "string"
                ? item.content
                : JSON.stringify(item?.content ?? {});
        });

        if (contentByLanguageOverride) {
            Object.keys(contentByLanguageOverride).forEach((key) => {
                map[key] = contentByLanguageOverride[key];
            });
        }

        return map;
    }

    function applyContentByLanguageToSection(section, contentByLanguageOverride = null) {
        if (!section || !contentByLanguageOverride) return section;

        const contents = getContentsArray(section).map((item) => {
            const key = toAbbrev(item);
            if (!key || !Object.prototype.hasOwnProperty.call(contentByLanguageOverride, key)) {
                return item;
            }

            return {
                ...item,
                content: contentByLanguageOverride[key],
            };
        });

        Object.keys(contentByLanguageOverride).forEach((key) => {
            if (contents.some((item) => toAbbrev(item) === key)) return;
            const languageRecord = resolveLanguageRecordForKey(key);
            contents.push({
                languageId: languageRecord.languageId,
                abbrev: languageRecord.abbrev,
                name: languageRecord.name,
                content: contentByLanguageOverride[key],
            });
        });

        return {
            ...section,
            contents,
        };
    }

    function buildPreviewSection(sourceSection, previewOptions = null) {
        if (!sourceSection) return null;

        const previewContentSource = previewOptions?.contentByLanguage ?? {
            ...draftContentByLanguage,
            ...historyPreviewContentByLanguage,
        };
        const previewContentByLanguage = toContentByLanguage(sourceSection, previewContentSource);
        const resolvedSectionId = Number(sourceSection?.sectionId || 0);
        const previewLanguage = previewOptions?.language
            || testState?.lang
            || languageDefinitions?.[0]?.key
            || "eng";
        const previewLanguageContent = previewContentByLanguage[previewLanguage] ?? "{}";

        return {
            ...Helpers.deepClone(sourceSection),
            id: resolvedSectionId,
            sectionId: resolvedSectionId,
            name: sourceSection?.sectionName,
            pages: resolveSectionPages(sourceSection, previewContentSource),
            sectionContentName: nameValue,
                contents: { ...previewContentByLanguage, [previewLanguage]: previewLanguageContent },
        };
    }

    useEffect(() => {
        if (!testState?.currentSection) {
            setPreviewSection(null);
            return;
        }

        setPreviewSection((prev) => {
            const next = buildPreviewSection(testState.currentSection);
            if (!next) return null;

            return next;
        });
    }, [draftContentByLanguage, historyPreviewContentByLanguage, languageDefinitions, nameValue, testState?.currentSection, testState?.lang]);

    function buildRealtimeResponsePayload(questionId, response) {
        const clientTime = DateTime.now().toISO();
        return {
            Name: previewSection?.name,
            FormId: previewSection?.formId,
            SectionId: previewSection?.id,
            Code: testState?.testData?.code || "demo",
            LanguageId: testState?.languageId,
            TestId: Number(testState?.testData?.id || 0),
            CompanyId: Number(testState?.testData?.companyId || 0),
            questionId: String(questionId),
            response: String(response ?? ""),
            ClientTime: clientTime,
            When: clientTime,
        };
    }

    function appendRealtimePayload(payload) {
        if (!payload) return;
        const next = [...previewRealtimePayloadsRef.current, payload];
        previewRealtimePayloadsRef.current = next;
    }

    async function onPreviewSectionCompleted(sectionEvent: SectionCompletedEvent) {
        const myOopsCount = Number(previewOopsCountRef.current || 0);
        const values = sectionEvent?.values || {};
        const code = testState?.testData?.code || "demo";
        const testId = Number(testState?.testData?.id || 0);
        const completionEvents = realtimeEventsAfterSectionComplete(
            previewSection?.name,
            previewSection?.formId,
            values,
            Number(sectionEvent?.response ?? myOopsCount),
        );

        const postSectionPayload = {
            TestId: testId,
            Name: previewSection?.name,
            SectionId: previewSection?.id,
            Success: true,
            Code: code,
            values,
            OopsCount: myOopsCount,
        };

        const nextRealtimePayloads = [
            ...previewRealtimePayloadsRef.current,
            ...completionEvents.map((event) => buildRealtimeResponsePayload(event.questionId, event.response)),
        ];
        previewRealtimePayloadsRef.current = nextRealtimePayloads;
        // Reset transient runtime state before showing payload details.
        setPreviewRuntimeResetKey((prev) => prev + 1);
        setPayloadPreview({ realtimePayloads: nextRealtimePayloads, postSectionPayload });
    }

    async function onPreviewNextAttempt(nextAttemptEvent: SectionRealtimePayload) {
        const issueCount = Number(nextAttemptEvent?.response || 0);
        if (issueCount <= 0) {
            return;
        }

        previewOopsCountRef.current = Number(previewOopsCountRef.current || 0) + 1;
        const realtimePayload = buildRealtimeResponsePayload(nextAttemptEvent?.questionId ?? "NextPage", issueCount);
        appendRealtimePayload(realtimePayload);
    }

    async function onPreviewAnswer(answerEvent: SectionRealtimePayload) {
        const questionId = answerEvent?.questionId;
        if (questionId === null || questionId === undefined) {
            return;
        }

        const realtimePayload = buildRealtimeResponsePayload(questionId, answerEvent?.response ?? "");
        appendRealtimePayload(realtimePayload);
    }

    async function onPreviewPageChanged(pageEvent: SectionRealtimePayload) {
        const realtimePayload = buildRealtimeResponsePayload(pageEvent?.questionId ?? "NextPage", pageEvent?.response ?? 0);
        appendRealtimePayload(realtimePayload);
    }

    const previewTestState = {
        isDebug: true,
        showData: false,
        lang: testState?.lang || "eng",
        languageId: Number(testState?.languageId || 1),
        currentSection: previewSection,
        lastSectionIdCompleted: previewSection?.id || 0,
        testData: {},
    };

    const previewLang = previewTestState.lang;
    const previewSectionControls = Helpers.normalizeSectionControlElements(
        previewSectionControlsByAbbrev?.[previewLang] ?? previewSectionControlsByAbbrev?.eng,
    );

    const previewSectionData = {
        testState: previewTestState,
        lang: previewTestState.lang,
        currentSection: previewSection,
        sectionControls: previewSectionControls,
        sectionControlsByAbbrev: previewSectionControlsByAbbrev,
    };

    const previewHooks: SectionRendererHooks = {
        onAnswer: onPreviewAnswer,
        onPageChanged: onPreviewPageChanged,
        onNextAttempt: onPreviewNextAttempt,
        onSectionCompleted: onPreviewSectionCompleted,
    };

    async function onSaveSectionData() {
        let resolvedSectionContentId = Number(testState?.currentSection?.sectionContentId || activeSectionContentId || sectionContentId || 0);
        const resolvedSectionId = Number(testState?.currentSection?.sectionId || sectionId || 0);
        const resolvedMasterSectionContentId = Number(masterSectionContentId || 0);
        const shouldForkFromMaster =
            Number(companyTestTypeId || 0) > 0 &&
            resolvedSectionId > 0 &&
            resolvedMasterSectionContentId > 0 &&
            resolvedSectionContentId === resolvedMasterSectionContentId;

        if (!resolvedSectionContentId) {
            toast.error("Unable to save section: missing section content id.");
            return false;
        }

        if (!hasChanges) {
            toast.info("No changes to save.");
            return false;
        }

        const getApiErrorMessage = (resp, error = null) => {
            const responseMessage = apiMessage(resp);
            if (responseMessage) {
                return responseMessage;
            }

            const errorMessage =
                apiMessage(error?.response?.data) ||
                error?.message;

            return typeof errorMessage === "string" && errorMessage.trim()
                ? errorMessage.trim()
                : "Failed to save section data.";
        };

        const htmlDrafts = draftHtmlByLanguageRef.current ?? draftHtmlByLanguage;
        const elementsDrafts = draftElementsByLanguageRef.current ?? draftElementsByLanguage;

        const committedDrafts = {};
        const allLanguageKeys = Array.from(new Set([
            ...Object.keys(elementsDrafts || {}),
            ...Object.keys(htmlDrafts || {}),
            ...languageDefinitions.map((item) => item.key),
        ])).filter(Boolean);

        for (const key of allLanguageKeys) {
            const elementsText = elementsDrafts?.[key] ?? getElementsTextForLanguage(key);
            const htmlValues = { ...(htmlDrafts?.[key] || {}) };
            const canonical = buildCanonicalRawContent(elementsText, htmlValues);
            if (!canonical.ok) {
                const languageName = languageDefinitions.find((item) => item.key === key)?.languageName || key;
                toast.error(`Invalid ${languageName} elements JSON format. ${canonical.error || "Please check your syntax."}`);
                return false;
            }
            committedDrafts[key] = canonical.formatted;
        }

        setDraftContentByLanguage(committedDrafts);
        const payload = buildComparableSection(testState?.currentSection, nameValue, committedDrafts);
        payload.contents = getContentsArray(payload).map((item) => {
            const key = toAbbrev(item);
            if (!key) return item;
            const canonical = buildCanonicalRawContent(
                elementsDrafts?.[key] ?? getElementsTextForLanguage(key),
                htmlDrafts?.[key] || {},
            );
            if (!canonical.ok) return item;
            return { ...item, content: canonical.minified };
        });

        if (typeof updateSpinner === "function") updateSpinner(true);
        try {
            if (shouldForkFromMaster) {
                const requestedCopyName = buildCustomCopyName(nameValue || testState?.currentSection?.sectionContentName || "Section Content");
                const copied = await copySectionContentForCompanyTestTypeAsync(
                    resolvedSectionContentId,
                    resolvedSectionId,
                    requestedCopyName
                );

                if (copied?.sectionContentId) {
                    resolvedSectionContentId = copied.sectionContentId;
                    setActiveSectionContentId(resolvedSectionContentId);
                    if (typeof copied.sectionContentName === "string" && copied.sectionContentName.trim()) {
                        setNameValue(copied.sectionContentName);
                    }

                    if (typeof onSectionContentForked === "function") {
                        onSectionContentForked(copied);
                    }

                    toast.info("Created a custom section content copy for this company test type.");
                }
            }

            payload.sectionContentId = resolvedSectionContentId;
            const resp = await RightTimeApi.putAsync("/SectionContent", payload);
            if (resp?.ok) {
                await loadSectionAsync(resolvedSectionContentId);
                return true;
            }
            toast.error(getApiErrorMessage(resp));
            return false;
        } catch (error) {
            toast.error(getApiErrorMessage(null, error));
            return false;
        } finally {
            if (typeof updateSpinner === "function") updateSpinner(false);
        }
    }

    const activeLanguageId = Number(testState?.languageId || 0);
    const activeLanguageVersions = versionsByLanguageId[activeLanguageId] || [];
    const activeSelectedVersionId =
        selectedVersionIdByLanguageId[activeLanguageId] ?? getCurrentContentVersionId(activeLanguageVersions);
    const viewingHistory = isViewingHistoricalContentVersion(activeSelectedVersionId, activeLanguageVersions);

    const footer = (
        <>
            <div className="sectionEditorFooterSection sectionEditorFooterJson">
                <label htmlFor="showJsonEditorToggle">Show JSON</label>
                <input
                    id="showJsonEditorToggle"
                    type="checkbox"
                    checked={showJsonEditor}
                    onChange={(e) => setShowJsonEditor(e.currentTarget.checked)}
                />
                {showJsonEditor && (
                    <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={onReformatJson}
                    >
                        Reformat Elements JSON
                    </button>
                )}
            </div>
            <div className="sectionEditorFooterSection sectionEditorFooterHistory">
                <ContentVersionHistorySelect
                    selectId="sectionEditorHistorySelect"
                    versions={activeLanguageVersions}
                    value={activeSelectedVersionId}
                    onChange={onSectionHistoryVersionChange}
                    label={
                        <ContentVersionHistoryButton
                            versions={activeLanguageVersions}
                            historyType="section"
                            onVersionNoteUpdated={(updated) => {
                                if (!activeLanguageId) return;
                                setVersionsByLanguageId((prev) => ({
                                    ...prev,
                                    [activeLanguageId]: mergeContentVersionNote(prev[activeLanguageId] ?? [], updated),
                                }));
                            }}
                        />
                    }
                />
            </div>
            <div className="sectionEditorFooterSection sectionEditorFooterName">
                <label htmlFor="sectionNameInput">Name</label>
                <input
                    id="sectionNameInput"
                    type="text"
                    value={nameValue}
                    onChange={onNameChange}
                />
            </div>
            <div className="sectionEditorFooterSection sectionEditorFooterActions">
                <button
                    type="button"
                    className={`btn btn-primary${hasChanges && !viewingHistory ? "" : " disabled"}`}
                    onClick={onSaveSectionData}
                    disabled={!hasChanges || viewingHistory}
                >
                    Save
                </button>
                <button type="button" className="btn btn-secondary" onClick={handleCloseModalAttempt}>
                    {hasChanges ? "Discard Changes" : "Close"}
                </button>
            </div>
        </>
    );

    const currentSection = testState?.currentSection;
    const activeLanguageKey = testState?.lang || languageDefinitions?.[0]?.key || "eng";
    const visibleHtmlFields = getVisibleHtmlFieldsForLanguage(activeLanguageKey);
    const htmlFieldTableRows = getExistingHtmlFieldsAcrossLanguages();
    const htmlEditorEnglishKey = resolveEnglishLanguageKey(languageDefinitions);
    const htmlEditorTargetLanguage = languageDefinitions.find((language) => language.key === htmlEditorLanguageKey);
    const showHtmlEditorTranslate =
        languageDefinitions.length > 1 &&
        !!htmlEditorLanguageKey &&
        htmlEditorLanguageKey !== htmlEditorEnglishKey;
    const canTranslateHtmlEditor =
        showHtmlEditorTranslate &&
        !!String(htmlEditorDraftByLanguage?.[htmlEditorEnglishKey] || "").trim();
    const htmlEditorPreviewLanguage = htmlEditorLanguageKey || activeLanguageKey;
    const htmlEditorPreviewSection = (() => {
        if (!isHtmlEditorOpen || !htmlEditorField || !currentSection) return null;
        if (!canRenderSection(currentSection.sectionName || currentSection.name)) return null;

        const languageKeys = languageDefinitions.map((language) => language.key).filter(Boolean);
        const appliedDrafts = applyHtmlEditorFieldDrafts({
            field: htmlEditorField,
            htmlEditorDraftByLanguage,
            draftHtmlByLanguage: draftHtmlByLanguageRef.current ?? draftHtmlByLanguage,
            draftHtmlFieldsByLanguage,
            draftElementsByLanguage: draftElementsByLanguageRef.current ?? draftElementsByLanguage,
            draftContentByLanguage,
            languageKeys,
            getElementsTextForLanguage,
            getVisibleHtmlFieldsForLanguage,
            buildCanonicalRawContent,
            orderHtmlFieldKeys,
        });

        return buildPreviewSection(currentSection, {
            contentByLanguage: appliedDrafts.draftContentByLanguage,
            language: htmlEditorPreviewLanguage,
        });
    })();
    const htmlEditorPreviewControls = Helpers.normalizeSectionControlElements(
        previewSectionControlsByAbbrev?.[htmlEditorPreviewLanguage] ?? previewSectionControlsByAbbrev?.eng,
    );
    const htmlEditorPreviewTestState = {
        ...previewTestState,
        lang: htmlEditorPreviewLanguage,
        currentSection: htmlEditorPreviewSection,
    };
    const htmlEditorPreviewSectionData = {
        testState: htmlEditorPreviewTestState,
        lang: htmlEditorPreviewLanguage,
        currentSection: htmlEditorPreviewSection,
        sectionControls: htmlEditorPreviewControls,
        sectionControlsByAbbrev: previewSectionControlsByAbbrev,
    };
    const headerActions = !isViewportExpanded
        ? [{
            key: "expand",
            icon: <SlSizeFullscreen size={16} />,
            onClick: enterViewportExpandedMode,
            title: "Expand modal to viewport",
            ariaLabel: "Expand modal to viewport",
            className: "sectionEditorHeaderExpandAction actionIcon",
        }]
        : [];

    const headerLanguageSelector = testState?.lang && languageDefinitions?.length > 0
        ? (
            <div className="sectionEditorHeaderSelectors">
                <ModalLanguageSelector
                    selectId="sectionEditorLanguageSelect"
                    languages={languageDefinitions}
                    value={testState.lang}
                    onChange={onSectionEditorLanguageChange}
                    getOptionValue={(language) => String(language.key ?? "")}
                    getOptionLabel={(language) => language.languageName || getLanguageDisplayName(language)}
                />
            </div>
        )
        : null;

    return (
        <>
            <Modal
                isOpen={!!isOpen}
                onClose={handleCloseModalAttempt}
                title={currentSection?.sectionName ? `Section Content Editor - ${currentSection.sectionName} (${sectionContentId})` : 'Section Content Editor'}
                size="xl"
                className={`sectionEditModal${isViewportExpanded ? " viewportExpanded" : ""}`}
                overlayClassName="sectionEditOverlay"
                headerContent={headerLanguageSelector}
                footer={footer}
                zIndex={1200}
                headerActions={headerActions}
            >
                {!testState || !currentSection ? (
                    <div>Loading section...</div>
                ) : (
                    <div
                        ref={splitContainerRef}
                        className={`sectionEditorContent${isViewportExpanded ? " viewportExpanded" : ""}${viewingHistory ? " viewingHistory" : ""}`}
                    >
                        {viewingHistory && !showJsonEditor ? (
                            <p className="contentVersionHistoryBanner sectionEditorHistoryBanner">
                                Viewing a previous version (read-only). Select the current version to edit or save.
                            </p>
                        ) : null}
                        {isViewportExpanded && (
                            <button
                                type="button"
                                className="sectionEditorExitExpand"
                                onClick={exitViewportExpandedMode}
                                title="Exit expanded mode"
                                aria-label="Exit expanded mode"
                            >
                                <AiOutlineFullscreenExit className="sectionEditorExitExpandIcon" size={22} />
                            </button>
                        )}
                        {showJsonEditor && (
                            <>
                                <div className="sectionEditorJsonPane">
                                    {viewingHistory ? (
                                        <p className="contentVersionHistoryBanner sectionEditorHistoryBanner">
                                            Viewing a previous version (read-only). Select the current version to edit or save.
                                        </p>
                                    ) : null}
                                    <div className="sectionEditorHtmlFieldToolbar horizontal">
                                        {htmlFieldTableRows.length > 0 ? htmlFieldTableRows.map((field) => (
                                            <button
                                                key={field}
                                                type="button"
                                                className="btn btn-sm btn-secondary"
                                                data-field={field}
                                                onClick={onHtmlTableEditClick}
                                                disabled={viewingHistory}
                                                title={`Edit ${field}`}
                                                aria-label={`Edit ${field}`}
                                            >
                                                {field}
                                            </button>
                                        )) : (
                                            <span className="sectionEditorHtmlFieldEmpty">No HTML fields found.</span>
                                        )}
                                    </div>

                                    <textarea
                                        id={`section-editor-${currentSection.id}`}
                                        ref={textareaRef}
                                        className="sectionEditorJsonTextarea"
                                        value={textareaValue}
                                        readOnly={viewingHistory}
                                        onChange={(e) => updateActiveLanguageContent(e.target.value)}
                                        placeholder="Edit elements JSON here..."
                                    />
                                </div>

                                <div
                                    role="separator"
                                    aria-orientation="vertical"
                                    onMouseDown={onSplitterMouseDown}
                                    className="sectionEditorSplitter"
                                />
                            </>
                        )}

                        <div className="sectionEditorPreviewPane">
                            {previewSection && textareaValue?.trim() ? (
                                <SectionRenderer
                                    key={`preview-runtime-${previewRuntimeResetKey}-${previewSection?.id || 0}`}
                                    sectionData={previewSectionData}
                                    hooks={previewHooks}
                                />
                            ) : previewSection ? (
                                <div className="sectionEditorNoContent">
                                    <p>Content not available for this language.</p>
                                    <p>Enable <strong>Show JSON</strong> to add content for this language.</p>
                                </div>
                            ) : (
                                <div>Preview unavailable.</div>
                            )}
                        </div>
                    </div>
                )}
            </Modal>

            {payloadPreview && (
                <TestOutputDataModal
                    isOpen={true}
                    onClose={closePayloadPreview}
                    payloadPreview={payloadPreview}
                    maxLineLength={myMaxLineLength}
                />
            )}

            <Modal
                isOpen={isHtmlEditorOpen}
                onClose={handleHtmlEditorCloseAttempt}
                title={htmlEditorField ? `HTML Editor - ${htmlEditorField}` : "HTML Editor"}
                size="xl"
                className="contentEditModal sectionHtmlEditorModal"
                zIndex={1400}
                footer={(
                    <>
                        <div className="footerLeft" />
                        <div className="footerCenter">
                            {languageDefinitions.length > 0 ? (
                                <ModalLanguageSelector
                                    selectId="sectionHtmlEditorLanguageSelect"
                                    languages={languageDefinitions}
                                    value={htmlEditorLanguageKey}
                                    onChange={onHtmlEditorLanguageChange}
                                    placement="footer"
                                    getOptionValue={(language) => String(language.key ?? "")}
                                    getOptionLabel={(language) => language.languageName || getLanguageDisplayName(language)}
                                />
                            ) : null}
                            {showHtmlEditorTranslate ? (
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={() => void onHtmlEditorTranslateAsync()}
                                    disabled={!canTranslateHtmlEditor || isHtmlEditorTranslating}
                                    title={
                                        canTranslateHtmlEditor
                                            ? `Translate English content to ${htmlEditorTargetLanguage?.languageName || getLanguageDisplayName(htmlEditorTargetLanguage) || htmlEditorLanguageKey}`
                                            : "English content is required before translating"
                                    }
                                >
                                    {isHtmlEditorTranslating ? "Translating..." : "Translate"}
                                </button>
                            ) : null}
                        </div>
                        <div className="footerRight">
                            <button
                                type="button"
                                className="btn btn-primary"
                                onClick={onSaveHtmlEditor}
                                disabled={!hasHtmlEditorUnsavedChanges()}
                            >
                                Accept
                            </button>
                            <button type="button" className="btn btn-secondary" onClick={handleHtmlEditorCloseAttempt}>
                                {hasHtmlEditorUnsavedChanges() ? "Discard Changes" : "Close"}
                            </button>
                        </div>
                    </>
                )}
            >
                <div ref={htmlEditorSplitContainerRef} className="contentEditGrid active">
                    <div className="contentEditSection contentEditSectionRaw">
                        <h4>HTML Content ({(htmlEditorLanguageKey || activeLanguageKey || "eng").toUpperCase()})</h4>
                        <textarea
                            className="rawContent sectionEditorHtmlTextarea"
                            value={htmlEditorDraftByLanguage?.[htmlEditorLanguageKey] || ""}
                            onChange={onHtmlEditorChange}
                            placeholder="Edit HTML content here..."
                        />
                    </div>

                    <div
                        role="separator"
                        aria-orientation="vertical"
                        onMouseDown={onHtmlEditorSplitterMouseDown}
                        className="contentEditSplitter"
                    />

                    <div className="contentEditSection contentEditSectionPreview">
                        <h4>Rendered Preview</h4>
                        {htmlEditorPreviewSection ? (
                            <div className="sectionHtmlEditorLivePreview">
                                <SectionRenderer
                                    key={`html-editor-preview-${htmlEditorField}-${htmlEditorPreviewLanguage}`}
                                    sectionData={htmlEditorPreviewSectionData}
                                    hooks={previewHooks}
                                />
                            </div>
                        ) : (
                            <div className="renderedContent">
                                {parseSanitizedHtml(htmlEditorDraftByLanguage?.[htmlEditorLanguageKey] || "")}
                            </div>
                        )}
                    </div>
                </div>
            </Modal>

            <ConfirmModal
                isOpen={showHtmlEditorCloseConfirm}
                onConfirm={onCloseHtmlEditor}
                onCancel={() => setShowHtmlEditorCloseConfirm(false)}
                title="Unsaved Changes"
                message="You have unsaved HTML changes. Are you sure you want to close without saving?"
                confirmText="Yes, Close Without Saving"
                zIndex={1401}
            />

            <ConfirmModal
                isOpen={showSectionCloseConfirm}
                onConfirm={() => void handleSectionCloseSave()}
                onCancel={() => setShowSectionCloseConfirm(false)}
                title="Unsaved Changes"
                message="You have unsaved section content changes. Save them before closing?"
                confirmText="Save"
                showCancelButton={false}
                extraActions={[
                    {
                        label: "Discard",
                        className: "btn-secondary",
                        onClick: handleSectionCloseDiscard,
                    },
                ]}
                zIndex={1301}
            />
        </>
    );
}


